/**
 * GapsInput.js
 * Two side-by-side resizable textarea cards + centred Analyse button.
 * Supports platform selector, compare mode toggle, and Pro gating.
 */
// eslint-disable-next-line no-unused-vars
import React from 'react';
import {
    Box, Paper, Typography, TextField, Button,
    Chip, Tooltip, alpha, useTheme, CircularProgress,
} from '@mui/material';
import {
    Compare as CompareIcon,
    TextFields as TextIcon,
    TrendingUp as TrendingUpIcon,
    Link as LinkIcon,
    Warning as WarningIcon,
    SwapHoriz as SwapIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { COMPARE_MODES, PLATFORMS } from './constants';

const _SOCIAL_DOMAINS = [
    'facebook.com', 'instagram.com', 'youtube.com', 'twitter.com',
    'x.com', 'tiktok.com', 'linkedin.com', 'reddit.com', 'pinterest.com',
];

function _isSocialUrl(val) {
    try {
        const url = new URL(val.trim());
        return _SOCIAL_DOMAINS.some(d => url.hostname.replace('www.', '').endsWith(d));
    } catch { return false; }
}

// ── Single labelled textarea ─────────────────────────────────────────────────
function ContentField({ label, subtitle, value, onChange, placeholder, color, icon, disabled, urlFetchAvailable }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isUrl = /^https?:\/\//.test(value.trim());
    const isSocial = isUrl && _isSocialUrl(value);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
                    bgcolor: alpha(color, isDark ? 0.2 : 0.1),
                    color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.71rem' }}>
                        {subtitle}
                    </Typography>
                </Box>
                {isUrl && !isSocial && urlFetchAvailable && (
                    <Chip icon={<LinkIcon sx={{ fontSize: 11 }} />} label="URL — will fetch" size="small"
                        sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 600, height: 20, fontSize: '0.67rem', borderRadius: '6px' }} />
                )}
                {isSocial && (
                    <Tooltip title="Social media URLs (Facebook, YouTube, Instagram, etc.) can't be scraped — paste the post text directly">
                        <Chip icon={<WarningIcon sx={{ fontSize: 11 }} />} label="Paste text instead" size="small"
                            sx={{ bgcolor: alpha('#ef4444', 0.1), color: '#ef4444', fontWeight: 600, height: 20, fontSize: '0.67rem', borderRadius: '6px' }} />
                    </Tooltip>
                )}
                {isUrl && !isSocial && !urlFetchAvailable && (
                    <Tooltip title="Install 'requests' on the backend to enable URL fetching">
                        <Chip icon={<WarningIcon sx={{ fontSize: 11 }} />} label="URL fetch off" size="small"
                            sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 600, height: 20, fontSize: '0.67rem', borderRadius: '6px' }} />
                    </Tooltip>
                )}
            </Box>

            {/* Social URL warning banner */}
            {isSocial && (
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    p: 1.25, borderRadius: '8px',
                    bgcolor: alpha('#ef4444', isDark ? 0.15 : 0.06),
                    border: `1px solid ${alpha('#ef4444', 0.2)}`,
                }}>
                    <WarningIcon sx={{ fontSize: 15, color: '#ef4444', mt: '1px', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.72rem', lineHeight: 1.5 }}>
                        <strong>Social media URLs can't be scraped</strong> — please copy and paste the post text directly instead.
                    </Typography>
                </Box>
            )}

            {/* Textarea */}
            <TextField
                fullWidth multiline rows={5}
                value={value} onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px', fontSize: '0.875rem', alignItems: 'flex-start',
                        '& fieldset': { borderColor: isSocial ? alpha('#ef4444', 0.4) : alpha(color, 0.2) },
                        '&:hover fieldset': { borderColor: isSocial ? alpha('#ef4444', 0.6) : alpha(color, 0.45) },
                        '&.Mui-focused fieldset': { borderColor: isSocial ? '#ef4444' : color, boxShadow: `0 0 0 3px ${alpha(isSocial ? '#ef4444' : color, 0.1)}` },
                    },
                    '& textarea': { resize: 'vertical' },
                }}
            />

            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.69rem' }}>
                Paste text directly, or a public article/blog URL — social media URLs are not supported
            </Typography>
        </Box>
    );
}

// ── Exported panel ────────────────────────────────────────────────────────────
export default function GapsInput({
    yourContent, onYourChange,
    compContent, onCompChange,
    onSubmit, loading, isValid,
    urlFetchAvailable,
    platform, onPlatformChange,
    compareMode, onCompareModeChange,
    isPro = false,
}) {
    const hasSocialUrl = _isSocialUrl(yourContent) || (compareMode === 'competitor' && _isSocialUrl(compContent));
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const showCompetitorField = compareMode === 'competitor';

    const cardSx = {
        p: 2.5, borderRadius: '12px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.05)',
        flex: 1,
    };

    return (
        <Box>
            {/* ── Compare mode toggle — Netflix cards ── */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                    Compare mode
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {COMPARE_MODES.map((m, i) => {
                        const active = compareMode === m.value;
                        const locked = m.pro && !isPro;
                        const modeColors = ['#6366f1', '#10b981', '#f59e0b'];
                        const color = modeColors[i] || '#6366f1';
                        return (
                            <Tooltip key={m.value} title={locked ? 'Pro feature — upgrade to unlock' : m.desc}>
                                <Box
                                    role="button"
                                    tabIndex={locked ? -1 : 0}
                                    aria-pressed={active}
                                    onClick={() => !locked && onCompareModeChange?.(m.value)}
                                    onKeyDown={(e) => !locked && (e.key === 'Enter' || e.key === ' ') && onCompareModeChange?.(m.value)}
                                    sx={{
                                        cursor: locked ? 'not-allowed' : 'pointer',
                                        opacity: locked ? 0.55 : 1,
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        borderRadius: '10px',
                                        px: 1.5, py: 0.75,
                                        border: `1.5px solid ${active ? alpha(color, 0.55) : alpha(theme.palette.divider, 0.5)}`,
                                        borderLeft: `4px solid ${active ? color : alpha(color, 0.3)}`,
                                        background: active
                                            ? isDark
                                                ? `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.07)} 100%)`
                                                : `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.04)} 100%)`
                                            : 'transparent',
                                        transition: 'all 0.2s ease',
                                        boxShadow: active
                                            ? `0 0 0 2px ${alpha(color, 0.18)}, 0 4px 14px ${alpha(color, 0.2)}`
                                            : 'none',
                                        position: 'relative', overflow: 'hidden',
                                        '&:hover': !locked ? {
                                            transform: 'translateY(-2px)',
                                            borderColor: alpha(color, 0.5),
                                            borderLeftColor: color,
                                            boxShadow: `0 6px 20px ${alpha(color, isDark ? 0.28 : 0.16)}`,
                                            background: isDark ? alpha(color, 0.1) : alpha(color, 0.05),
                                        } : {},
                                        '&::after': active ? {
                                            content: '""', position: 'absolute',
                                            top: -8, right: -8, width: 36, height: 36, borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(color, 0.28)}, transparent 70%)`,
                                            pointerEvents: 'none',
                                        } : {},
                                    }}
                                >
                                    {locked && <LockIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />}
                                    <Typography variant="caption" sx={{
                                        fontWeight: active ? 700 : 500,
                                        fontSize: '0.78rem',
                                        color: active ? (isDark ? alpha(color, 0.9) : color) : locked ? 'text.disabled' : 'text.secondary',
                                        userSelect: 'none', whiteSpace: 'nowrap', transition: 'color 0.2s',
                                    }}>
                                        {m.label}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>

            {/* ── Platform selector — Netflix cards ── */}
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                    Platform <Typography component="span" variant="caption" sx={{ color: 'text.disabled', fontWeight: 400 }}>(optional)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {PLATFORMS.map(p => {
                        const active = platform === p;
                        const platformColors = { Instagram: '#e1306c', YouTube: '#ff4500', TikTok: '#00c8ae', LinkedIn: '#0a66c2', Twitter: '#1d9bf0', Facebook: '#1877f2' };
                        const color = platformColors[p] || '#10b981';
                        return (
                            <Box
                                key={p}
                                role="button"
                                tabIndex={0}
                                onClick={() => onPlatformChange?.(active ? null : p)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPlatformChange?.(active ? null : p)}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: '10px',
                                    px: 1.25, py: 0.6,
                                    border: `1.5px solid ${active ? alpha(color, 0.55) : alpha(theme.palette.divider, 0.5)}`,
                                    borderLeft: `4px solid ${active ? color : alpha(color, 0.3)}`,
                                    background: active
                                        ? isDark
                                            ? `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.07)} 100%)`
                                            : `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.04)} 100%)`
                                        : 'transparent',
                                    transition: 'all 0.2s ease',
                                    boxShadow: active ? `0 2px 10px ${alpha(color, 0.2)}` : 'none',
                                    '&:hover': {
                                        background: isDark ? alpha(color, 0.1) : alpha(color, 0.05),
                                        borderColor: alpha(color, 0.45),
                                        borderLeftColor: color,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 14px ${alpha(color, 0.18)}`,
                                    },
                                }}
                            >
                                <Typography variant="caption" sx={{
                                    fontSize: '0.76rem',
                                    fontWeight: active ? 700 : 500,
                                    color: active ? color : 'text.secondary',
                                    userSelect: 'none', whiteSpace: 'nowrap', transition: 'color 0.2s',
                                }}>
                                    {p}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ── Self Best / Niche Benchmark info banner ── */}
            {compareMode !== 'competitor' && (
                <Box sx={{
                    mb: 2, p: 1.5, borderRadius: '10px',
                    bgcolor: alpha('#6366f1', isDark ? 0.1 : 0.06),
                    border: `1px solid ${alpha('#6366f1', 0.2)}`,
                }}>
                    <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 600, fontSize: '0.83rem' }}>
                        {compareMode === 'self_best'
                            ? "We'll automatically compare against your top 5 posts when you run analysis."
                            : "We'll generate an AI-crafted ideal post for your niche as the benchmark."}
                    </Typography>
                </Box>
            )}

            {/* ── Content input fields ── */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
                {/* Your content */}
                <Paper elevation={0} sx={cardSx}>
                    <ContentField
                        label="Your Content"
                        subtitle="Paste your social media post or blog"
                        value={yourContent}
                        onChange={onYourChange}
                        placeholder={'Paste your social media post, blog excerpt…\n\nor enter a URL: https://yoursite.com/post'}
                        color="#6366f1"
                        icon={<TextIcon sx={{ fontSize: 17 }} />}
                        disabled={loading}
                        urlFetchAvailable={urlFetchAvailable}
                    />
                </Paper>

                {/* VS separator — only when competitor field is visible */}
                {showCompetitorField && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexDirection: { xs: 'row', md: 'column' },
                        gap: 1, py: { xs: 0, md: 1.5 },
                    }}>
                        <Box sx={{
                            display: { xs: 'none', md: 'block' },
                            width: '1px', flex: 1,
                            background: `linear-gradient(to bottom, transparent, ${theme.palette.divider}, transparent)`,
                        }} />
                        <Box sx={{
                            width: 40, height: 40, flexShrink: 0, borderRadius: '10px',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.07)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <SwapIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </Box>
                        <Box sx={{
                            display: { xs: 'none', md: 'block' },
                            width: '1px', flex: 1,
                            background: `linear-gradient(to bottom, ${theme.palette.divider}, transparent)`,
                        }} />
                    </Box>
                )}

                {/* Competitor — hidden for self_best / niche_benchmark */}
                {showCompetitorField && (
                    <Paper elevation={0} sx={cardSx}>
                        <ContentField
                            label="Competitor / Top Performer"
                            subtitle="Paste a top-performing post or competitor"
                            value={compContent}
                            onChange={onCompChange}
                            placeholder={'Paste a top-performing post or competitor content…\n\nor enter a URL: https://competitor.com/viral-post'}
                            color="#10b981"
                            icon={<TrendingUpIcon sx={{ fontSize: 17 }} />}
                            disabled={loading}
                            urlFetchAvailable={urlFetchAvailable}
                        />
                    </Paper>
                )}
            </Box>

            {/* Analyse button */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!isValid || hasSocialUrl}
                    size="large"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CompareIcon />}
                    sx={{
                        borderRadius: '10px', px: 5, py: 1.5,
                        fontWeight: 800, fontSize: '0.95rem',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 6px 24px rgba(99,102,241,0.35)',
                        textTransform: 'none',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5855eb, #7c3aed)',
                            boxShadow: '0 10px 32px rgba(99,102,241,0.45)',
                            transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                            background: alpha(theme.palette.text.disabled, 0.1),
                            color: theme.palette.text.disabled,
                            boxShadow: 'none',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {loading ? 'Analysing Gaps…' : 'Analyse Gaps'}
                </Button>
                {loading && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5, fontWeight: 500, fontSize: '0.83rem' }}>
                        AI is comparing cross-platform engagement patterns…
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
